import i18n from '../i18n';
import { base64Decode, base64Encode, isBase64 } from '../utils/base64';
import { AIJob } from './AIJob';
import { AdoptionRequest } from './AdoptionRequest';
import { Book, translateBookForTransport, translateBookFromTransport } from './Book';
import { BookOutline } from './BookOutline';
import { Chapter, getChapterById } from './Chapter';
import { CourseSection } from './CourseSection';
import { DrmSettings } from './DrmSettings';
import { Item } from './Item';
import { OnixRequest } from './OnixRequest';
import { Page, getPageById } from './Page';
import { Person } from './Person';
import { Publisher } from './Publisher';
import { Question } from './Question';
import { EditionReferenceType, InitialReference } from './Reference';

export type EditionSource = {
  startLabel: string;
  endLabel: string;
} & InitialReference<EditionReferenceType>;

export enum UploadMethod {
  ONIX = 'ONIX',
  MANUAL = 'MANUAL',
}

export interface Edition extends Item {
  number?: number;
  coverImageUrl?: string;
  isbn?: string;

  bookId?: number;
  book?: Book;

  bookOutlineId?: number;
  bookOutline?: BookOutline;

  questions?: Question[];

  key?: string;

  isImageAccessible?: boolean;

  editionName?: string;
  authors?: string[];
  language?: string;
  numberOfPages?: number;

  publisherId?: number;
  publisher?: Publisher;

  isActive: boolean;
  deactivatedAt?: Date;

  title: string;
  description: string;

  drmSettings: DrmSettings;

  hasChapterReadingComprehension: boolean;
  hasChapterStudyTool: boolean;
  hasChapterQuiz: boolean;

  url?: string;

  canAssess?: boolean;

  mimeType?: string;

  contentLength?: number;

  aiJobs?: AIJob[];
  hasFlashcards?: boolean;
  hasQuestions?: boolean;
  hasSlides?: boolean;

  aiEngine?: string;

  version?: string;

  percentOnboard?: number;

  uploadMethod?: UploadMethod;

  adoptionRequests?: AdoptionRequest[];

  onixRequests?: OnixRequest[];

  demoId?: number;

  demo?: CourseSection;

  demoStudentId?: number;
  demoStudent?: Person;
  demoInstructorId?: number;

  demoInstructor?: Person;
}

export const displayEdition = (edition?: Edition): string => {
  if (!edition) {
    return '-';
  }

  let output = '';

  if (!isNaN(Number(edition.editionName))) {
    const suffix = ['st', 'nd', 'rd'][((edition.number ?? 0) % 10) - 1] || 'th';
    output = `${edition.editionName}${suffix}`;
  } else {
    output = edition.editionName ?? '';
  }

  if (!output.includes('Ed')) {
    output = `${output} Ed`;
  }

  return output;
};

export const translateEditionFromTransport = (attributes: any): any => {
  const { book } = attributes;
  return {
    ...attributes,
    book: book ? translateBookFromTransport(book) : undefined,
    description: isBase64(attributes.description)
      ? base64Decode(attributes.description)
      : attributes.description,
  };
};

export const translateEditionForTransport = (attributes: any): any => {
  const { book } = attributes;
  return {
    ...attributes,
    book: book ? translateBookForTransport(book) : undefined,
    description: !isBase64(attributes.description)
      ? base64Encode(attributes.description)
      : attributes.description,
  };
};

export const translateEditionCloneFromTransport = (attributes: any): any => {
  const { description } = attributes;
  if (!description) {
    return attributes;
  }

  return {
    ...attributes,
    description: isBase64(description) ? base64Decode(description) : description,
  };
};

export const translateEditionCloneForTransport = (attributes: any): any => {
  const { description } = attributes;
  if (!description) {
    return attributes;
  }

  return {
    ...attributes,
    description: !isBase64(description) ? base64Encode(description) : description,
  };
};

export const displayAuthors = (edition?: Edition): string => {
  if (!edition || !edition?.authors || edition.authors?.length === 0) {
    return '-';
  }

  if (edition.authors?.length === 1) {
    return edition.authors[0];
  }

  if (edition.authors?.length === 2) {
    return `${edition.authors[0]} & ${edition.authors[1]}`;
  }

  if (edition.authors?.length > 2) {
    return `${edition.authors[0]} et al.`;
  }

  return '-';
};

export const displayEditionSource = (edition: Edition, source: EditionSource) => {
  const { referenceType, startId, endId, startLabel, endLabel } = source;

  if (!edition?.bookOutline) {
    throw Error('Edition does not have a book outline');
  }

  const chapters: Chapter[] = edition?.bookOutline?.chapters ?? [];
  const pages: Page[] = edition?.bookOutline?.pages ?? [];

  if (referenceType === EditionReferenceType.ALL) {
    return i18n.t('all');
  }

  if (startId === endId) {
    if (referenceType === EditionReferenceType.CHAPTER) {
      const chapter = getChapterById(chapters ?? [], startId);
      return `${i18n.t('chapter')} ${chapter?.label ?? startLabel}`;
    } else if (referenceType === EditionReferenceType.PAGE) {
      const page = getPageById(pages ?? [], startId);
      return `${i18n.t('page')} ${page?.label ?? startLabel}`;
    }
  }

  if (referenceType === EditionReferenceType.CHAPTER) {
    const startChapter = getChapterById(chapters ?? [], startId);
    const endChapter = getChapterById(chapters ?? [], endId);
    return `${i18n.t('chapters')} ${startChapter?.label ?? startLabel} - ${endChapter?.label ?? endLabel}`;
  } else if (referenceType === EditionReferenceType.PAGE) {
    const startPage = getPageById(pages ?? [], startId);
    const endPage = getPageById(pages ?? [], endId);
    return `${i18n.t('pages')} ${startPage?.label ?? startLabel} - ${endPage?.label ?? endLabel}`;
  }

  return '-';
};

export const isEdition = (item: any): boolean => {
  return item?.bookOutlineId !== undefined ? true : false;
};
